import * as IntentLauncher from "expo-intent-launcher";
import * as Linking from "expo-linking";
import * as LocalAuthentication from "expo-local-authentication";
import * as SecureStore from "expo-secure-store";
import type { AndroidSymbol, SFSymbol } from "expo-symbols";
import { Platform } from "react-native";

import { authStorage } from "@/lib/feathers/storage";
import type {
    FontAwesome6ProRegularIconName,
    FontAwesome6ProSolidIconName,
} from "@react-native-vector-icons/fontawesome6-pro";

const BIOMETRIC_UNLOCK_DISABLED_KEY = "biometric_unlock_disabled";
/** Must match @feathersjs/authentication-client default storageKey. */
const ACCESS_TOKEN_KEY = "feathers-jwt";

const BIOMETRIC_PROMPT_MESSAGE = "Unlock the app to access your account";

export type BiometricSymbolName = {
    ios: SFSymbol;
    android: AndroidSymbol;
    web: AndroidSymbol;
};

export async function hasBiometricHardware(): Promise<boolean> {
    if (Platform.OS === "web") {
        return false;
    }

    const types = await LocalAuthentication.supportedAuthenticationTypesAsync();
    return types.length > 0;
}

export async function isBiometricUnlockAvailable(): Promise<boolean> {
    if (Platform.OS === "web") {
        return false;
    }

    const [hasHardware, isEnrolled] = await Promise.all([
        LocalAuthentication.hasHardwareAsync(),
        LocalAuthentication.isEnrolledAsync(),
    ]);

    return hasHardware && isEnrolled;
}

export async function isBiometricUnlockDisabled(): Promise<boolean> {
    if (Platform.OS === "web") {
        return false;
    }

    const value = await SecureStore.getItemAsync(BIOMETRIC_UNLOCK_DISABLED_KEY);
    return value === "true";
}

export async function setBiometricUnlockDisabled(
    disabled: boolean,
): Promise<void> {
    if (Platform.OS === "web") {
        return;
    }

    if (disabled) {
        await SecureStore.setItemAsync(BIOMETRIC_UNLOCK_DISABLED_KEY, "true");
        return;
    }

    await SecureStore.deleteItemAsync(BIOMETRIC_UNLOCK_DISABLED_KEY);
}

export async function isBiometricUnlockRequired(): Promise<boolean> {
    if (!(await isBiometricUnlockAvailable())) {
        return false;
    }

    return !(await isBiometricUnlockDisabled());
}

export async function hasStoredAccessToken(): Promise<boolean> {
    const token = await authStorage.getItem(ACCESS_TOKEN_KEY);
    return Boolean(token);
}

export async function promptBiometricUnlock(): Promise<boolean> {
    const result = await LocalAuthentication.authenticateAsync({
        promptMessage: BIOMETRIC_PROMPT_MESSAGE,
        requireConfirmation: false,
    });

    return result.success;
}

export type BiometricDisplayInfo = {
    label: string;
    /** Native symbols for `expo-symbols` (`faceid` / `touchid` on iOS). */
    symbol: BiometricSymbolName;
    /** FontAwesome fallback when SymbolView cannot render. */
    fallbackIcon: FontAwesome6ProRegularIconName | FontAwesome6ProSolidIconName;
};

const DEFAULT_BIOMETRIC_DISPLAY: BiometricDisplayInfo = {
    label: "Biometrics",
    symbol: {
        ios: "touchid",
        android: "fingerprint",
        web: "fingerprint",
    },
    fallbackIcon: "fingerprint",
};

let cachedBiometricDisplay: BiometricDisplayInfo | null = null;

function resolveBiometricDisplayInfo(
    types: LocalAuthentication.AuthenticationType[],
): BiometricDisplayInfo {
    if (
        types.includes(
            LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION,
        )
    ) {
        return {
            label: Platform.OS === "ios" ? "Face ID" : "Biometrics",
            symbol: {
                ios: "faceid",
                android: "face",
                web: "face",
            },
            fallbackIcon: "face-viewfinder",
        };
    }

    if (types.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
        return {
            label: Platform.OS === "ios" ? "Touch ID" : "Fingerprint",
            symbol: {
                ios: "touchid",
                android: "fingerprint",
                web: "fingerprint",
            },
            fallbackIcon: "fingerprint",
        };
    }

    if (types.includes(LocalAuthentication.AuthenticationType.IRIS)) {
        return {
            label: "Biometrics",
            symbol: {
                ios: "eye",
                android: "visibility",
                web: "visibility",
            },
            fallbackIcon: "eye",
        };
    }

    return DEFAULT_BIOMETRIC_DISPLAY;
}

/** Device hardware type; stable for the app lifetime — load once. */
export async function loadBiometricDisplayInfo(): Promise<BiometricDisplayInfo> {
    if (cachedBiometricDisplay) {
        return cachedBiometricDisplay;
    }

    if (Platform.OS === "web") {
        cachedBiometricDisplay = DEFAULT_BIOMETRIC_DISPLAY;
        return cachedBiometricDisplay;
    }

    const types = await LocalAuthentication.supportedAuthenticationTypesAsync();
    cachedBiometricDisplay = resolveBiometricDisplayInfo(types);
    return cachedBiometricDisplay;
}

export async function shouldPromptBiometricUnlock(): Promise<boolean> {
    if (!(await hasStoredAccessToken())) {
        return false;
    }

    return isBiometricUnlockRequired();
}

/**
 * Opens OS settings where biometrics can be enabled.
 * iOS: this app's Settings (Face ID toggle).
 * Android: device Security settings (fingerprint lives there, not on the app page).
 */
export async function openSystemBiometricSettings(): Promise<void> {
    if (Platform.OS === "android") {
        for (const action of [
            IntentLauncher.ActivityAction.SECURITY_SETTINGS,
            IntentLauncher.ActivityAction.SETTINGS,
        ]) {
            try {
                await IntentLauncher.startActivityAsync(action);
                return;
            } catch {
                // Screen may be missing on this OEM or API level.
            }
        }
    } else {
        await Linking.openSettings();
    }
}
